import {Pivot, PivotItem, SelectionMode, Stack, StackItem,} from "@fluentui/react";
import * as React from "react";
import {FC, useCallback, useMemo, useRef, useState} from "react";
import axios from "axios";
import {useTranslation} from "react-i18next";
import {ArbitraryFieldEntryType, ArbitraryFieldLocationEnum, DocumentType, DocumentTypeArbitraryField,} from "../API";
import {TenantResource} from "../enums/TenantResource";
import {CMEditor} from "./CMElement";
import {MultilineTextComponent} from "./MultilineTextComponent";
import {TenantList} from "./TenantList";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {
    CheckboxElement,
    EnumDropdownElement,
    ITenantFormLayout,
    NumberElement,
    TextFieldElement,
} from "./OnixElements";
import {AlertApiError} from "./ShowApiError";
import {ITenantFormLayoutWithSort} from "./TenantBulkItemList";
import {ListItemCheckbox} from "./AsCheckMark";

const baseContext = `

// interface XMLBuilderOptions {
//   version: "1.0"
//   encoding: string | undefined
//   standalone: boolean | undefined
//   keepNullNodes: boolean
//   keepNullAttributes: boolean
//   ignoreConverters: boolean
//   skipWhitespaceOnlyText: boolean
//   convert: ConvertOptions
//   defaultNamespace: {
//     ele: undefined | null | string,
//     att: undefined | null | string
//   }
//   namespaceAlias: {
//     html: "http://www.w3.org/1999/xhtml",
//     xml: "http://www.w3.org/XML/1998/namespace",
//     xmlns: "http://www.w3.org/2000/xmlns/",
//     mathml: "http://www.w3.org/1998/Math/MathML",
//     svg: "http://www.w3.org/2000/svg",
//     xlink: "http://www.w3.org/1999/xlink"
//     [key: string]: string | null
//   }
//   invalidCharReplacement: string | ((char: string, offset: number, str: string) => string) | undefined
//   parser: ParserOptions | undefined
// }

interface XMLBuilder2Created{
  ele(namespace: string | null, name: string, attributes?: AttributesObject): XMLBuilder2Created;
  ele(name: string, attributes?: AttributesObject): XMLBuilder2Created;
  ele(obj: ExpandObject): XMLBuilder2Created;
  ele(content: string): XMLBuilder2Created;

  att(namespace: string | null, name: string, value: string): XMLBuilder2Created
  att(name: string, value: string): XMLBuilder2Created
  att(obj: AttributesObject): XMLBuilder2Created

  com(content: string): XMLBuilder2Created;
  txt(content: string): XMLBuilder2Created;

  doc(): XMLBuilder2Created;
  root(): XMLBuilder2Created;
  up(): XMLBuilder2Created;
  prev(): XMLBuilder2Created;
  next(): XMLBuilder2Created;
  first(): XMLBuilder2Created;
  last(): XMLBuilder2Created;
}

interface XMLBuilder2Base {
  create: () => XMLBuilder2Created;
}

declare const xmlBuilder2: XMLBuilderBase;

`;

const itemAndPriceContext = `
${baseContext}

interface SerialNumberData{
  serials: {
    [key: string]: {
      item: THeStockSerialNosPagedParametrized$BaseQuery$THeStockSerialNosPagedParametrizedConnection$THeStockSerialNo,
      quantity: number
    }
  }[],
  usedSerials: string[],
  quantityReached: boolean,
};

declare const ctx: {
  item: [
    number,
    THeSetItem,
    CreateGenericDocumentFromAnotherItemDtoInput,
    SerialNumberData,
    any
  ],
  itemPrice: number
};

declare const locale: string;
`;

const itemAndPriceTHeMoveContext = `
${baseContext}

declare const ctx: {
  item: [
    null,
    THeMoveItem
  ],
  itemPrice: number
};

declare const locale: string;
`;

const itemAndPriceTHeOrderContext = `
${baseContext}

declare const ctx: {
  item: [
    null,
    THeOrderItem
  ],
  itemPrice: number
};

declare const locale: string;
`;

const itemAndPriceTHeSetItemContext = `
${baseContext}

declare const ctx: {
  item: [
    null,
    THeSetItem
  ],
  itemPrice: number
};

declare const locale: string;
`;

const itemOnlyTHeSetItemContext = `
${baseContext}

declare const ctx: {
  item: [
    null,
    THeSetItem
  ]
};

declare const locale: string;
`;

export const itemInWarehouseOverviewContext = `
${baseContext}

declare const ctx: {
  item: THeSetItem,
  definition: LabelDefinition
};

declare const locale: string;
`;

export const pdfTHeMoveContext = `
${baseContext}

declare const ctx: {
    singleTHeMove: THeMove,
    singleVHeMoveFiscalisation: VHeMoveFiscalisation,
    vOpMoveBank: VOpMoveBank,
    tHeMoveItemExciseOuts: THeMoveItemExciseOut[],
};

declare const locale: string;
`;

export const filterExistingOrderDocumentsBasedOnScannedModifierCharacterContext = `
${baseContext}

declare const ctx: {
    item: THeOrderItem,
    modifierCharacter: string
}

declare const locale: string;
`;

export const filterExistingMoveDocumentsBasedOnScannedModifierCharacterContext = `
${baseContext}

declare const ctx: {
    item: THeMoveItem,
    modifierCharacter: string
}

declare const locale: string;
`;

export const customerContext = `
${baseContext}

declare const ctx: {
    customer: THeSetSubj
}

declare const locale: string;
`;

export const submitDocumentContext = `
${baseContext}

declare const ctx: {
    customer?: THeSetSubj,
    items?: {
        0: number,
        1: THeSetItem,
        2: CreateGenericDocumentFromAnotherItemDtoInput,
        3: SerialNumberData,
        4: {
            [key: string]: any
        }
    }[],
    shadowItems?: {
        0: number,
        1: THeSetItem,
        2: CreateGenericDocumentFromAnotherItemDtoInput,
        3: SerialNumberData,
        4: {
            [key: string]: any
        }
    }[],
    description?: string,
    uid?: string,
    deadlineDays?: number,
    acDelivery?: string,
    acPayMethod?: string,
    acDeliveryName?: string,
    acPayMethodName?: string,
    acPayMethodFiscalGroup?: string,
    date?: Date,
    contact?: string,
    contactEmail?: string,
    warehouse?: THeSetSubj,
    receivingWarehouse?: THeSetSubj,
}

declare const locale: string;
`;

export const documentAnalysisContext = `
${baseContext}

declare const ctx: {
    customer?: THeSetSubj,
    items?: {
        0: number,
        1: THeSetItem,
        2: CreateGenericDocumentFromAnotherItemDtoInput,
        3: SerialNumberData,
        4: {
            [key: string]: any
        }
    }[],
    shadowItems?: {
        0: number,
        1: THeSetItem,
        2: CreateGenericDocumentFromAnotherItemDtoInput,
        3: SerialNumberData,
        4: {
            [key: string]: any
        }
    }[],
    document: {
        dateTime?: string,
        localCreatedAt?: string,
        adDate?: string,
        adDateDue?: string,
        docId?: number,
        fromDocId?: number,
        pairId?: number,
        fromDocAcKey?: string,
        fromDocAcKeys?: string[],
        isUnsavedButFinalized?: boolean,
        syncError?: string,
        docType?: string,
        note?: string,
        acDoc1?: string,
        adDateDoc1?: string,
        acDoc2?: string,
        adDateDoc2?: string,
        documentCounter?: number,
        currency?: string,
        isDirty?: boolean,
        updateExisting?: boolean,
        customer?: THeSetSubj,
        items?: {
            0: number,
            1: THeSetItem,
            2: CreateGenericDocumentFromAnotherItemDtoInput,
            3: SerialNumberData,
            4: {
                [key: string]: any
            }
        }[],
        shadowItems?: {
            0: number,
            1: THeSetItem,
            2: CreateGenericDocumentFromAnotherItemDtoInput,
            3: SerialNumberData,
            4: {
                [key: string]: any
            }
        }[],
        description?: string,
        uid?: string,
        deadlineDays?: number,
        acDelivery?: string,
        acPayMethod?: string,
        acDeliveryName?: string,
        acPayMethodName?: string,
        acPayMethodPaymentType?: string,
        acPayMethodFiscalGroup?: string,
        date?: string,
        contact?: string,
        contactEmail?: string,
        pallet?: {
            shortCode?: string,
            userId?: number,
            acRegion?: string,
            ssccCode?: string,
            acSubject?: string
        },
        woexItemPlannedQuantity?: number,
        woexItemProducedQuantity?: number,
        woexIdent?: THeSetItem,
        warehouse?: THeSetSubj,
        receivingWarehouse?: THeSetSubj,
        additionalEntryFields?: {
            [key: string]: any
        },
        additionalFields?: {
            [key: string]: any
        },
        arbitraryData?: {
            [key: string]: any
        }
    }
}

declare const locale: string;
`;

export const addDocumentItemContext = `
${baseContext}

declare const ctx: {
    document: {
        customer?: THeSetSubj,
        items?: {
            0: number,
            1: THeSetItem,
            2: CreateGenericDocumentFromAnotherItemDtoInput,
            3: SerialNumberData,
            4: {
                [key: string]: any
            }
        }[],
        shadowItems?: {
            0: number,
            1: THeSetItem,
            2: CreateGenericDocumentFromAnotherItemDtoInput,
            3: SerialNumberData,
            4: {
                [key: string]: any
            }
        }[],
        description?: string,
        uid?: string,
        deadlineDays?: number,
        acDelivery?: string,
        acPayMethod?: string,
        acDeliveryName?: string,
        acPayMethodName?: string,
        acPayMethodFiscalGroup?: string,
        date?: Date,
        contact?: string,
        contactEmail?: string,
        warehouse?: THeSetSubj,
        receivingWarehouse?: THeSetSubj,
    },
    item: {
        0: number,
        1: THeSetItem,
        2: CreateGenericDocumentFromAnotherItemDtoInput,
        3: SerialNumberData,
        4: {
            [key: string]: any
        }
    },
}

declare const locale: string;
`;

const ArbitraryFieldTypeDefinitions: {
    [key in ArbitraryFieldLocationEnum]: string;
} = {
    [ArbitraryFieldLocationEnum.ArticlePickerItemListItemAfterPrice]:
    itemAndPriceTHeSetItemContext,
    [ArbitraryFieldLocationEnum.THeMoveListItemAfterPrice]:
    itemAndPriceTHeMoveContext,
    [ArbitraryFieldLocationEnum.THeOrderListItemAfterPrice]:
    itemAndPriceTHeOrderContext,
    [ArbitraryFieldLocationEnum.NewDocumentItemListAfterPrice]:
    itemAndPriceContext,
    [ArbitraryFieldLocationEnum.NewDocumentItemListAfterQuantity]:
    itemAndPriceContext,
    [ArbitraryFieldLocationEnum.NewDocumentItemItemListAfterPrice]:
    itemAndPriceContext,
    [ArbitraryFieldLocationEnum.NewDocumentItemReplacement]: itemAndPriceContext,
    [ArbitraryFieldLocationEnum.THeMoveListItemReplacement]:
    itemAndPriceTHeMoveContext,
    [ArbitraryFieldLocationEnum.THeOrderListItemReplacement]:
    itemAndPriceTHeOrderContext,
    [ArbitraryFieldLocationEnum.NewDocumentItemPackageQuantity]:
    itemOnlyTHeSetItemContext,
    [ArbitraryFieldLocationEnum.PDFAnClerk]: pdfTHeMoveContext,
    [ArbitraryFieldLocationEnum.FilterExistingOrderDocumentsBasedOnScannedModifierCharacter]:
    filterExistingOrderDocumentsBasedOnScannedModifierCharacterContext,
    [ArbitraryFieldLocationEnum.FilterExistingMoveDocumentsBasedOnScannedModifierCharacter]:
    filterExistingMoveDocumentsBasedOnScannedModifierCharacterContext,
    [ArbitraryFieldLocationEnum.NewDocumentCustomerError]: customerContext,
    [ArbitraryFieldLocationEnum.OnSubmitDocumentShowError]: submitDocumentContext,
    [ArbitraryFieldLocationEnum.OnSubmitDocumentShowWarning]:
    submitDocumentContext,
    [ArbitraryFieldLocationEnum.OnAddNewDocumentItemShowError]:
    addDocumentItemContext,
    [ArbitraryFieldLocationEnum.OnAddNewDocumentItemShowWarning]:
    addDocumentItemContext,
    [ArbitraryFieldLocationEnum.AfterHeaderCustomerAnalysis]: customerContext,
    [ArbitraryFieldLocationEnum.AfterHeaderDocumentAnalysis]:
    documentAnalysisContext,
    [ArbitraryFieldLocationEnum.AdditionalDocumentEntryField]: "",
    [ArbitraryFieldLocationEnum.AdditionalPDFHeaderField]: ""
};

interface DocumentTypeArbitraryFieldProps {
    documentType: DocumentType;
}

export const DocumentTypeArbitraryFields: FC<
    DocumentTypeArbitraryFieldProps
> = ({documentType}) => {
    const {t} = useTranslation();

    const arbitraryDocTypeFieldBaseDto = useMemo(
        () => ({
            documentTypeId: documentType?.id,
            companySettingsId: documentType?.companySettingsId,
        }),
        [documentType],
    );

    const arbitraryDocTypeFieldForm: ITenantFormLayout<DocumentTypeArbitraryField> =
        useMemo(
            () => ({
                name: {
                    type: new TextFieldElement({}),
                },
                order: {
                    type: new NumberElement({}),
                },
                fieldType: {
                    type: new EnumDropdownElement({
                        enumData: ArbitraryFieldEntryType,
                        showCancel: true,
                    }),
                    hiddenIf: dto => !dto?.arbitraryFieldLocation || ![ArbitraryFieldLocationEnum.AdditionalPDFHeaderField, ArbitraryFieldLocationEnum.AdditionalDocumentEntryField].includes(dto.arbitraryFieldLocation)
                },
                fieldReference: {
                    type: new TextFieldElement({}),
                    hiddenIf: dto => !dto?.arbitraryFieldLocation || ![ArbitraryFieldLocationEnum.AdditionalPDFHeaderField, ArbitraryFieldLocationEnum.AdditionalDocumentEntryField].includes(dto.arbitraryFieldLocation)
                },
                fieldRequired: {
                    type: new CheckboxElement({}),
                    hiddenIf: dto => !dto?.arbitraryFieldLocation || ![ArbitraryFieldLocationEnum.AdditionalDocumentEntryField].includes(dto.arbitraryFieldLocation)
                },
                fieldBaseValue: {
                    type: new TextFieldElement({}),
                    hiddenIf: dto => !dto?.arbitraryFieldLocation || ![ArbitraryFieldLocationEnum.AdditionalPDFHeaderField, ArbitraryFieldLocationEnum.AdditionalDocumentEntryField].includes(dto.arbitraryFieldLocation)
                },
                prefillFromField: {
                    type: new TextFieldElement({}),
                    hiddenIf: dto => !dto?.arbitraryFieldLocation || ![ArbitraryFieldLocationEnum.AdditionalDocumentEntryField].includes(dto.arbitraryFieldLocation)
                },
                arbitraryFieldLocation: {
                    type: new EnumDropdownElement({
                        enumData: ArbitraryFieldLocationEnum,
                        showCancel: true,
                    }),
                },
            }),
            [],
        );

    const arbitraryDocTypeInitialQuery = useMemo(
        () => ({documentTypeId: {Equals: documentType?.id}}),
        [documentType],
    );

    const arbitraryDocTypeFieldColumns = useMemo(
        () =>
            MakeTenantColumns<DocumentTypeArbitraryField>(
                {
                    name: {},
                    order: {},
                    fieldType: {
                        render: (x) =>
                            <span>{x.fieldType !== null && x.fieldType !== undefined ? t(ArbitraryFieldEntryType[x.fieldType]) : "-"}</span>,
                    },
                    fieldReference: {},
                    fieldRequired: {
                        render: (x) => <ListItemCheckbox checked={x.fieldRequired} />
                    },
                    fieldBaseValue: {},
                    prefillFromField: {},
                    arbitraryFieldLocation: {
                        render: (x) => (
                            <span>
                {x.arbitraryFieldLocation !== undefined &&
                x.arbitraryFieldLocation !== null
                    ? t(ArbitraryFieldLocationEnum[x.arbitraryFieldLocation])
                    : ""}
              </span>),},},
                t,
            ),
        [t],
    );

    const [selectedArbitraryDocTypeField, setSelectedArbitraryDocTypeField] =
        useState<Partial<DocumentTypeArbitraryField> | undefined>(undefined);

    const reloadSingleArbitraryDocTypeFieldRef = useRef<
        | ((
        item: DocumentTypeArbitraryField,
    ) => Promise<DocumentTypeArbitraryField>)
        | undefined
    >(undefined);

    const saveSelectedArbitraryDocTypeField = useCallback(
        async (value: string): Promise<void> => {
            if (selectedArbitraryDocTypeField) {
                try {
                    const {data} = await axios.put(
                        `ArbitraryDocumentTypeFields/${selectedArbitraryDocTypeField.id}`,
                        {
                            ...selectedArbitraryDocTypeField,
                            jsValue: value,
                        },
                    );
                    reloadSingleArbitraryDocTypeFieldRef.current?.(data);
                } catch (e) {
                    AlertApiError(e);
                }
            }
        },
        [selectedArbitraryDocTypeField, reloadSingleArbitraryDocTypeFieldRef],
    );

    return (
        <Stack style={{width: "100%", minWidth: "100%", flex: 1, flexGrow: 1}}>
            {/* <TenantBulkItemList<
      DocumentTypeArbitraryField,
      DocumentTypeArbitraryField,
      DocumentTypeArbitraryField
    >
      resource={TenantResource.ArbitraryDocumentTypeFields}
      initialQuery={arbitraryDocTypeInitialQuery}
      titleProps={{ title: "Proizvoljna polja" as any }}
      fields={arbitraryDocTypeFieldFields}
      baseDto={arbitraryDocTypeFieldBaseDto}
    /> */}
            <Stack>
                <StackItem
                    grow={1}
                    style={{width: "100%", minWidth: "100%", flex: 1, flexGrow: 1}}
                >
                    <TenantList<
                            DocumentTypeArbitraryField,
                            DocumentTypeArbitraryField,
                            DocumentTypeArbitraryField
                        >
                        resource={TenantResource.ArbitraryDocumentTypeFields}
                        query={arbitraryDocTypeInitialQuery}
                        title={"Proizvoljna polja" as any}
                        columns={arbitraryDocTypeFieldColumns}
                        baseDto={arbitraryDocTypeFieldBaseDto}
                        addingTitle={"Dodavanje proizvoljnog polja" as any}
                        editingTitle={"Uređivanje proizvoljnog polja" as any}
                        form={arbitraryDocTypeFieldForm}
                        selectionChanged={setSelectedArbitraryDocTypeField}
                        selectionModeOverride={SelectionMode.single}
                        reloadSingleRef={reloadSingleArbitraryDocTypeFieldRef}
                        allowedGroupKeys={{
                            arbitraryFieldLocation: (val) =>
                                val !== undefined && val !== null
                                    ? ArbitraryFieldLocationEnum[val]
                                    : " - ",
                        }}
                    />
                </StackItem>
                <StackItem
                    grow={1}
                    style={{width: "100%", minWidth: "100%", flex: 1, flexGrow: 1}}
                >
                    <div style={{width: "100%", flex: 1, flexGrow: 1}}>
                        {selectedArbitraryDocTypeField && (
                            <Pivot>
                                <PivotItem headerText="JavaScript">
                                    <CMEditor
                                        key={`selectedArbitraryDocTypeField.id-${selectedArbitraryDocTypeField.id}`}
                                        endData={
                                            (selectedArbitraryDocTypeField as any)?.jsValue ?? ""
                                        }
                                        saveFile={saveSelectedArbitraryDocTypeField}
                                        language="javascript"
                                        disabled={!selectedArbitraryDocTypeField}
                                        minHeight="500px"
                                        tsDefinitions={
                                            selectedArbitraryDocTypeField.arbitraryFieldLocation !==
                                            undefined &&
                                            selectedArbitraryDocTypeField.arbitraryFieldLocation !==
                                            null
                                                ? ArbitraryFieldTypeDefinitions[
                                                    selectedArbitraryDocTypeField.arbitraryFieldLocation
                                                    ]
                                                : undefined
                                        }
                                    />
                                </PivotItem>
                                <PivotItem headerText="Text">
                                    <MultilineTextComponent
                                        key={`selectedArbitraryDocTypeField.id-${selectedArbitraryDocTypeField.id}`}
                                        rows={10}
                                        value={(selectedArbitraryDocTypeField as any)?.value ?? ""}
                                        onChange={(v, nv) => {
                                            setSelectedArbitraryDocTypeField({
                                                ...selectedArbitraryDocTypeField,
                                                value: nv ?? "",
                                            });
                                        }}
                                    />
                                </PivotItem>
                            </Pivot>
                        )}
                    </div>
                </StackItem>
            </Stack>
        </Stack>
    );
};
